import React, {ReactNode} from "react";
// import Markdown from 'markdown-to-jsx';
import { MuiMarkdown as Markdown } from 'mui-markdown';
import { styled } from '@mui/material';
import capitalize from 'lodash/capitalize';
import { Box, Container, Grid, Typography, IconButton } from '@mui/material';
import { Facebook, Twitter, Instagram, LinkedIn } from '@mui/icons-material';
import GoldBox from './atoms/GoldBox';
import {Footer as FooterProps, FooterText as FooterTextProps, FooterSocial as FooterSocialProps, FooterLinks as FooterLinksProps} from '../types/app'
import ComponentContainer from "./atoms/ComponentContainer";
import Link from './atoms/Link';

interface ColumnTitleProps {
    children: ReactNode
    [key: string]: any; // For any additional props]
}

const ColumnTitle : React.FC<ColumnTitleProps> = ({children}) => {
    return (
        <Typography variant="h6" gutterBottom>
            {children}
        </Typography>
    )
}

interface ColumnContentProps {
    children: ReactNode,
    [key: string]: any; // For any additional props
}

const ColumnContent : React.FC<ColumnContentProps> = ({children}) => {
    return (
        <Typography variant="body2" sx={{fontWeight:100}}>
            {children}
        </Typography>
    )
}

const SOCIALS_ENABLED   = false;

const ensureSingleLeadingSlash = (path: string): string => `/${path.replace(/^\/+/, '')}`;

const DotBackground = ({children} : {children : ReactNode}) => (
    <Box 
        sx={{
            backgroundImage     : `radial-gradient(#D3D3D3 5%, #5A5A5A 5%)`,
            backgroundPosition  : '0 0',
            backgroundSize      : '15px 15px',
            height              : '100%',
            width               : '100%',
            mx                  : 'auto'
        }
    }>
        {children}
    </Box>
)

const FooterText : React.FC<FooterTextProps> = (props) => {
    return (
        <Box data-sb-object-id={props.contentful_id}>
            <ColumnTitle data-sb-field-path="heading">
                {props.heading}
            </ColumnTitle>
            <ColumnContent data-sb-field-path="body">
                <Markdown options={{ forceBlock: true }}>
                    {props.body.body}
                </Markdown>
            </ColumnContent>
        </Box>
    )
}

const FooterLinks : React.FC<FooterLinksProps> = (props) => {
    return (
        <Box data-sb-object-id={props.contentful_id}>
            <ColumnTitle data-sb-field-path="heading">
                {props.heading}
            </ColumnTitle>
            <Box data-sb-field-path="links" data-sb-object-id = {props.links.contentful_id} sx={{"& > * + *" : {mt:1}}}>
                {
                    (props.links.links || []).map(({slug,contentful_id}) => (
                        <Box key={contentful_id}>
                            <Link to={ensureSingleLeadingSlash(`/${slug}`)}>
                                {capitalize(slug !== '/' ? slug : "home")}
                            </Link>
                        </Box>
                    ))
                }
            </Box>
        </Box>
    )
}

const FooterSocial : React.FC<FooterSocialProps> = (props) => {
    return (
        <Box data-sb-object-id={props.contentful_id}>
            <ColumnTitle data-sb-field-path="heading">
                {props.heading}
            </ColumnTitle>
            <IconButton disabled={!SOCIALS_ENABLED} color="inherit" href="#" aria-label="Facebook">
                <Facebook />
            </IconButton>
            <IconButton disabled={!SOCIALS_ENABLED} color="inherit" href="#" aria-label="Twitter">
                <Twitter />
            </IconButton>
            <IconButton disabled={!SOCIALS_ENABLED} color="inherit" href="#" aria-label="Instagram">
                <Instagram />
            </IconButton>
            <IconButton disabled={!SOCIALS_ENABLED} color="inherit" href="#" aria-label="LinkedIn">
                <LinkedIn />
            </IconButton>
        </Box>
    )
}

const componentMap = {
    ContentfulFooterText    : FooterText,
    ContentfulFooterLinks   : FooterLinks,
    ContentfulFooterSocial  : FooterSocial
}

const FooterContainer = styled(Box)`
    display : flex;
    flexDirection : row;
    color : white;
`
export const Footer : React.FC<FooterProps> = (props) => (
    <ComponentContainer fullWidth={true} contentful_id={props.contentful_id} sx={{position:'relative',p:0}}>
        <FooterContainer >
            <DotBackground>
                <GoldBox style={{height:20,width:'100%'}} />
                <Container maxWidth="lg" sx={{py:2,mt:2}}>
                    <Box sx={{minHeight:200}}>
                        <Grid container spacing={4} data-sb-field-path="sections" >
                            {
                                (props.sections || []).map((section,ix) => {
                                    const Component = componentMap[section.__typename] as any;
                                    return (
                                        <Grid key={ix} item xs={12} sm={6} md={3}>
                                            <Component {...section} />
                                        </Grid>
                                    )
                                })
                            }
                        </Grid>
                    </Box>
                    <Box flexGrow={1} />
                    <Box my={4} textAlign="center">
                        <ColumnContent>
                            &copy; {new Date().getFullYear()} {SITE_METADATA.companyName}. All rights reserved.
                        </ColumnContent>
                    </Box>
                </Container>
            </DotBackground>
        </FooterContainer>
    </ComponentContainer>
)

export default Footer;